/* eslint-disable react/no-array-index-key */
import { EventGroup as ApiEventGroup } from '@/api/enumerations';
import { ControlActivityEvents, SessionEvent } from '@/api/oversight/types';
import { toRouteGroup } from '@/services/route';

import {
  describeBootstrapEvent,
  describeHomeEvent,
  describeRouteGroup,
  describeSessionEvent,
} from '../../utils';
import {
  timelineDataAdmUserDelegatedAccess,
  timelineDataClient,
  timelineDataSectionsAdmUser,
  TimelineItemProps,
} from './data';
import { Container } from './styles';
import { TimelineGroup } from './TimelineGroup';
import { TimelineItem } from './TimelineItem';
import {
  entityNameKey,
  isNamedEntityGroup,
  useEntityNames,
} from './useEntityNames';

export type TimelineEvent = SessionEvent | ControlActivityEvents;

type TimelineSource =
  | 'delegated_access'
  | 'section_client'
  | 'section_adm_user';

type EventGroup = {
  key: string;
  label: string;
  entityId?: number;
  items: TimelineItemProps[];
};

type TimelineProps = {
  source?: TimelineSource;
  events?: TimelineEvent[];
  currentDelegatedSessionId?: number | null;
  onActivityClick?: (delegatedSessionId: number) => void;
};

// Control activity events do not carry audit flags nor error details.
const isSessionEvent = (event: TimelineEvent): event is SessionEvent =>
  'is_auditable' in event;

const getDelegatedSessionId = (
  event: TimelineEvent,
  source?: TimelineSource,
  currentDelegatedSessionId?: number | null
): number | undefined => {
  if (source === 'delegated_access') return undefined;

  const { delegated_session_id: delegatedSessionId } = event.event_metadata;

  if (delegatedSessionId == null) return undefined;

  if (delegatedSessionId === currentDelegatedSessionId) return undefined;

  return delegatedSessionId;
};

const formatEventTime = (isoDate: string): string =>
  new Date(isoDate).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

// `event_type` is sent as `<group>:<route>`; the group is the screen the call
// belongs to. Events without the group half are the bare route, ids and all:
// what proto-front still sends, what this console recorded before the group,
// and what the mobile app sends. Auditable events are never purged, so that
// shape stays in the data for good.
const splitEventType = (event: TimelineEvent): [string, string] => {
  const eventType = event.event_type || '';
  const separatorIndex = eventType.indexOf(':');

  // Same toRouteGroup the apps normalize with when they build the header — if
  // the two sides ever drift, the keys stop matching.
  if (separatorIndex < 0) {
    const route = eventType.trim();

    if (!route) return ['unknown', ''];

    return [toRouteGroup(route), route];
  }

  return [
    eventType.slice(0, separatorIndex).trim() || 'unknown',
    eventType.slice(separatorIndex + 1),
  ];
};

// The group holds `{id}` exactly where the route holds the real value, so
// lining the two up recovers the id without parsing the endpoint's query.
const getEntityId = (group: string, route: string): number | undefined => {
  const groupSegments = group.split('/');
  const routeSegments = route.split('/');

  if (groupSegments.length !== routeSegments.length) return undefined;

  const placeholderIndex = groupSegments.lastIndexOf('{id}');

  if (placeholderIndex < 0) return undefined;

  const id = Number(routeSegments[placeholderIndex]);

  return Number.isFinite(id) ? id : undefined;
};

export function Timeline({
  source,
  events,
  currentDelegatedSessionId,
  onActivityClick,
}: TimelineProps): JSX.Element {
  const sourceMap = {
    delegated_access: timelineDataAdmUserDelegatedAccess,
    section_client: timelineDataClient,
    section_adm_user: timelineDataSectionsAdmUser,
  };

  const toTimelineItem = (
    event: TimelineEvent,
    groupKey: string
  ): TimelineItemProps => {
    const delegatedSessionId = getDelegatedSessionId(
      event,
      source,
      currentDelegatedSessionId
    );
    const describeEvent =
      {
        bootstrap: describeBootstrapEvent,
        home: describeHomeEvent,
      }[groupKey] ?? describeSessionEvent;

    return {
      id: event.id,
      time: formatEventTime(event.occurred_at),
      message: describeEvent(event.method, event.endpoint),
      status: event.status_category === 'success' ? 'success' : 'error',
      isAuditable: isSessionEvent(event) ? event.is_auditable : false,
      isLink: delegatedSessionId !== undefined,
      delegatedSessionId,
      errorType: isSessionEvent(event)
        ? event.event_metadata?.error_type
        : undefined,
      errorMessage: isSessionEvent(event)
        ? event.event_metadata?.error_message
        : undefined,
    };
  };

  // Consecutive events collapse by label, not by raw key: `/oversight/clients`
  // and `/oversight/clients/customer-details/12` are different keys that read as
  // the same place, and two identical headers in a row help nobody. The label map
  // is therefore the single knob for granularity — name two screens differently
  // and they split apart. Only consecutive events merge, so returning to a screen
  // later in the session still reads as a separate visit. Bootstrap calls fire
  // alongside the screen's own on mount, so a bootstrap group in between does
  // not split that screen in two.
  const groups: EventGroup[] = [];

  (events ?? []).forEach((event) => {
    const [key, route] = splitEventType(event);
    const label = describeRouteGroup(key);
    const currentGroup = groups[groups.length - 1];
    const previousGroup = groups[groups.length - 2];

    if (currentGroup?.label === label) {
      currentGroup.items.push(toTimelineItem(event, key));
      return;
    }

    if (
      currentGroup?.key === ApiEventGroup.BOOTSTRAP &&
      previousGroup?.label === label
    ) {
      previousGroup.items.push(toTimelineItem(event, key));
      return;
    }

    groups.push({
      key,
      label,
      entityId: isNamedEntityGroup(key) ? getEntityId(key, route) : undefined,
      items: [toTimelineItem(event, key)],
    });
  });

  const entityNames = useEntityNames(
    groups.flatMap(({ key, entityId }) =>
      entityId === undefined ? [] : [{ group: key, id: entityId }]
    )
  );

  if (!events) {
    const items = sourceMap[source ?? 'section_client'] || [];

    return (
      <Container>
        {items.map((item, index) => (
          <TimelineItem
            key={index}
            id={item.id}
            time={item.time}
            message={item.message}
            status={item.status}
            isAuditable={item.isAuditable}
            isLink={item.isLink}
            isFirst={index === 0}
            isLast={index === items.length - 1}
            errorType={item.errorType}
            errorMessage={item.errorMessage}
          />
        ))}
      </Container>
    );
  }

  return (
    <Container>
      {groups.map((group, index) => {
        const entityName =
          group.entityId === undefined
            ? undefined
            : entityNames[
                entityNameKey({ group: group.key, id: group.entityId })
              ];

        return (
          <TimelineGroup
            key={`${group.key}-${index}`}
            label={entityName ? `${group.label} (${entityName})` : group.label}
            items={group.items}
            isFirst={index === 0}
            isLast={index === groups.length - 1}
            onActivityClick={onActivityClick}
          />
        );
      })}
    </Container>
  );
}
